/**
 * Inline editor for a participant's login identifiers — primary email and
 * phone plus any number of extra ("also works") emails and phones. Saving
 * runs the real /log lookup for each identifier and shows a pass/fail so
 * you know it works before you close it.
 */
import { useState } from "react";
import { FloppyDisk, X, Plus, Trash, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const IdentifierEditor = ({ participant, token, onSaved, onClose }) => {
  const [email, setEmail] = useState(participant.email || "");
  const [phone, setPhone] = useState(participant.phone || participant.phone_digits || "");
  const [emailAliases, setEmailAliases] = useState(participant.email_aliases || []);
  const [phoneAliases, setPhoneAliases] = useState(participant.phone_aliases || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [checks, setChecks] = useState(null);

  const setAt = (list, setList) => (i, v) => setList(list.map((x, j) => (j === i ? v : x)));
  const removeAt = (list, setList) => (i) => setList(list.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true); setError(""); setChecks(null);
    try {
      const r = await fetch(`${BACKEND}/api/admin/participants/${participant.id}/identifiers`, {
        method: "PUT",
        headers: { "X-Admin-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          phone,
          email_aliases: emailAliases.map((v) => v.trim()).filter(Boolean),
          phone_aliases: phoneAliases.map((v) => v.trim()).filter(Boolean),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setChecks(d.login_checks || []);
      onSaved?.();
    } catch (e) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const AliasRows = ({ list, setList, kind, placeholder }) => (
    <div className="space-y-1.5">
      {list.map((v, i) => (
        <div key={i} className="flex gap-1.5">
          <input
            data-testid={`${kind}-alias-input-${i}`}
            value={v}
            onChange={(e) => setAt(list, setList)(i, e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-black/50 border border-white/15 focus:border-[#FF1B91] outline-none px-2.5 py-1.5 text-[12px] text-white"
          />
          <button
            type="button"
            data-testid={`remove-${kind}-alias-${i}`}
            onClick={() => removeAt(list, setList)(i)}
            className="px-2 text-white/40 hover:text-[#FF1B91]"
            title="Remove"
          >
            <Trash size={13} weight="bold" />
          </button>
        </div>
      ))}
      <button
        type="button"
        data-testid={`add-${kind}-alias`}
        onClick={() => setList([...list, ""])}
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/45 hover:text-[#FF1B91] font-bold"
      >
        <Plus size={11} weight="bold" /> Add {kind === "email" ? "another email" : "another phone"}
      </button>
    </div>
  );

  return (
    <div data-testid={`identifier-editor-${participant.id}`} className="bg-black/50 border border-[#FF1B91]/40 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF1B91]">
          Logins for {participant.name}
        </div>
        <button type="button" data-testid={`close-editor-${participant.id}`} onClick={onClose} className="text-white/40 hover:text-white">
          <X size={14} weight="bold" />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-white/45">Primary email</div>
          <input
            data-testid={`edit-email-${participant.id}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@email.com"
            className="w-full bg-black/50 border border-white/15 focus:border-[#FF1B91] outline-none px-2.5 py-1.5 text-[12px] text-white"
          />
          <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-white/45 pt-2">Also works</div>
          <AliasRows list={emailAliases} setList={setEmailAliases} kind="email" placeholder="old@email.com" />
        </div>

        <div className="space-y-1.5">
          <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-white/45">Primary phone</div>
          <input
            data-testid={`edit-phone-${participant.id}`}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(631) 555-0123"
            className="w-full bg-black/50 border border-white/15 focus:border-[#FF1B91] outline-none px-2.5 py-1.5 text-[12px] text-white"
          />
          <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-white/45 pt-2">Also works</div>
          <AliasRows list={phoneAliases} setList={setPhoneAliases} kind="phone" placeholder="631-555-0199" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid={`save-identifiers-${participant.id}`}
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-[#FF1B91] text-white bg-[#FF1B91]/10 hover:bg-[#FF1B91]/25 disabled:opacity-40 px-4 py-2"
        >
          <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save logins"}
        </button>
        {error && (
          <span data-testid={`editor-error-${participant.id}`} className="inline-flex items-center gap-1.5 text-[11px] text-[#FF1B91]">
            <WarningCircle size={13} weight="bold" /> {error}
          </span>
        )}
      </div>

      {checks && (
        <div data-testid={`editor-checks-${participant.id}`} className="border border-white/10 bg-black/30 px-3 py-2 space-y-1">
          <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-white/45">Tested against the real /log lookup</div>
          {checks.map((c) => (
            <div key={c.identifier} className={`text-[11px] flex items-center gap-1.5 ${c.works ? "text-emerald-300" : "text-[#FF1B91]"}`}>
              {c.works ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} weight="fill" />}
              <span className="font-mono">{c.identifier}</span>
              {!c.works && <span className="text-white/50">— {c.reason || "not resolving"}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-white/35 leading-relaxed">
        Phones need all 10 digits — any format is fine. An identifier already on someone else's record is
        rejected so a login can never open two people's records.
      </div>
    </div>
  );
};

export default IdentifierEditor;
